import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Order } from '../../../store/order/api/interface';
import { LocationCoordinates } from '../../../client/services';
import { toastHandler } from '../../../utils/toastHandler';
import { customIcon } from '../../../utils/icon';
import { extractCoordinatesFromGoogleMapsUrl } from '../../../utils/googleMapsUrlParser';

interface LocationEditDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null | undefined;
  onLocationUpdate: (updatedOrder: Order) => void;
}

interface LocationData {
  latitude: number | null;
  longitude: number | null;
}

interface LocationErrors {
  latitude?: string;
  longitude?: string;
}

const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

const LocationEditDialog: React.FC<LocationEditDialogProps> = ({
  open,
  onClose,
  order,
  onLocationUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LocationData>({
    latitude: null,
    longitude: null,
  });
  const [googleMapsUrl, setGoogleMapsUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string | null>(null);

  const [errors, setErrors] = useState<LocationErrors>({});

  useEffect(() => {
    if (open && order?.shippingAddress) {
      setFormData({
        latitude: order.shippingAddress.latitude || null,
        longitude: order.shippingAddress.longitude || null,
      });
      setError(null);
    }
  }, [open, order]);

  const handleMapClick = (lat: number, lng: number) => {
    setFormData({
      latitude: lat,
      longitude: lng,
    });
    
    // Clear errors when user selects location
    setErrors({});
  };

  const handleInputChange = (field: keyof LocationData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(event.target.value) || null;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setGoogleMapsUrl(url);
    setUrlError(null);
  };

  const handleExtractFromUrl = async () => {
    if (!googleMapsUrl.trim()) {
      setUrlError('Please enter a Google Maps URL');
      return;
    }

    setIsExtracting(true);
    setUrlError(null);

    try {
      const result = await extractCoordinatesFromGoogleMapsUrl(googleMapsUrl);
      
      if (result.success && result.latitude !== null && result.longitude !== null) {
        setFormData({
          latitude: result.latitude,
          longitude: result.longitude,
        });
        setUrlError(null);
        setErrors({});
        
        // Show success message
        const successMessage = `✅ Coordinates extracted: ${result.latitude}, ${result.longitude}`;
        
        toastHandler({ type: 'success', message: successMessage });
        
        // Clear the URL input after successful extraction
        setGoogleMapsUrl('');
      } else {
        const errorMessage = result.error || 'Failed to extract coordinates';
        setUrlError(errorMessage);
        toastHandler({ type: 'error', message: `❌ ${errorMessage}` });
      }
    } catch (error) {
      const errorMessage = 'Error processing URL';
      setUrlError(errorMessage);
      toastHandler({ type: 'error', message: `❌ ${errorMessage}` });
    } finally {
      setIsExtracting(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: LocationErrors = {};

    if (formData.latitude === null || formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Valid latitude is required (-90 to 90)';
    }

    if (formData.longitude === null || formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Valid longitude is required (-180 to 180)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !order) {
      return;
    }

    // Type guard to ensure values are not null after validation
    if (formData.latitude === null || formData.longitude === null) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare location coordinates data for the new API
      const locationCoordinatesData = {
        locationCoordinates: {
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude),
        }
      };

      console.log('Updating location coordinates via new API:', locationCoordinatesData);
      const response = await LocationCoordinates.patch(order.id, locationCoordinatesData);
      
      if (response && response.success) {
        // Update the order with the new coordinates
        const updatedOrder = {
          ...order,
          shippingAddress: {
            ...order.shippingAddress,
            latitude: response.locationCoordinates.latitude,
            longitude: response.locationCoordinates.longitude,
          },
        };
        
        onLocationUpdate(updatedOrder);
        onClose();
        toastHandler({
          message: response.message || 'Location coordinates updated successfully',
          type: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error updating location coordinates:', error);
      setError(error.message || 'Failed to update location coordinates');
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  // Default center coordinates (Doha, Qatar)
  const defaultCenter: [number, number] = [25.3548, 51.1839];
  const mapCenter: [number, number] = [
    typeof formData.latitude === 'number' ? formData.latitude : defaultCenter[0],
    typeof formData.longitude === 'number' ? formData.longitude : defaultCenter[1]
  ];

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Edit Location Coordinates</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set location coordinates by clicking on the map, entering manually, or extracting from a Google Maps URL.
          </Typography>

          {/* Google Maps URL Input */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Extract from Google Maps URL
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Google Maps URL"
                placeholder="Paste Google Maps URL here"
                value={googleMapsUrl}
                onChange={handleUrlChange}
                error={!!urlError}
                helperText={urlError || "Supports full URLs and shortened URLs"}
                sx={{ flex: 1 }}
                disabled={isExtracting || loading}
              />
              <Button
                variant="contained"
                onClick={handleExtractFromUrl}
                disabled={!googleMapsUrl.trim() || isExtracting || loading}
                sx={{ minWidth: 120, ml: 1 }}
              >
                {isExtracting ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                    Extracting...
                  </>
                ) : (
                  'Extract'
                )}
              </Button>
            </Box>
          </Box>
          
          {/* Interactive Map */}
          <Box sx={{ height: 400, width: '100%', mb: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              attributionControl={false}
            >
              <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
              <MapClickHandler onLocationSelect={handleMapClick} />
              {formData.latitude !== null && formData.longitude !== null && 
               typeof formData.latitude === 'number' && typeof formData.longitude === 'number' && (
                <Marker 
                  position={[formData.latitude, formData.longitude]}
                  icon={customIcon}
                />
              )}
            </MapContainer>
          </Box>

          {/* Coordinate Input Fields */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latitude !== null ? formData.latitude : ''}
                onChange={handleInputChange('latitude')}
                error={!!errors.latitude}
                helperText={errors.latitude || 'Range: -90 to 90'}
                disabled={loading}
                inputProps={{
                  step: 'any',
                  min: -90,
                  max: 90,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.longitude !== null ? formData.longitude : ''}
                onChange={handleInputChange('longitude')}
                error={!!errors.longitude}
                helperText={errors.longitude || 'Range: -180 to 180'}
                disabled={loading}
                inputProps={{
                  step: 'any',
                  min: -180,
                  max: 180,
                }}
              />
            </Grid>
          </Grid>

          {/* Preview Section */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Location:
            </Typography>
            <Typography variant="body2">
              <strong>Latitude:</strong> {formData.latitude !== null && typeof formData.latitude === 'number' 
                ? formData.latitude.toFixed(6) 
                : formData.latitude !== null 
                  ? formData.latitude 
                  : 'Not set'}
            </Typography>
            <Typography variant="body2">
              <strong>Longitude:</strong> {formData.longitude !== null && typeof formData.longitude === 'number' 
                ? formData.longitude.toFixed(6) 
                : formData.longitude !== null 
                  ? formData.longitude 
                  : 'Not set'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading || isExtracting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || isExtracting}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationEditDialog;
